import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { sendRequest } from "../utils/api";

async function checkInternetConnection(): Promise<boolean> {
    try {
        // If the user is not authenticated, we don't need
        // to check the protected API endpoint.
        const authToken = localStorage.getItem("authToken");

        if (!authToken) {
            return navigator.onLine;
        }

        await sendRequest("GET", "/ping", false);

        return true;
    } catch {
        return false;
    }
}

type NetworkContextType = {
    isOnline: boolean;
};

const NetworkContext = createContext<NetworkContextType>({
    isOnline: true,
});

export const NetworkProvider: React.FC<{
    interval?: number;
    children: React.ReactNode;
}> = ({ interval = 3000, children }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [isOnline, setIsOnline] = useState<boolean>(
        navigator.onLine
    );

    useEffect(() => {
        const NO_NETWORK_PATH = "/no-network";

        const saveCurrentLocation = () => {
            // Don't save the no-network page itself.
            if (location.pathname === NO_NETWORK_PATH) {
                return;
            }

            const currentPath =
                location.pathname +
                location.search +
                location.hash;

            sessionStorage.setItem(
                "network_previous_path",
                currentPath
            );
        };

        const goToNoNetworkPage = () => {
            // Already on the no-network page.
            if (window.location.pathname === NO_NETWORK_PATH) {
                return;
            }

            saveCurrentLocation();

            navigate(NO_NETWORK_PATH, {
                replace: true,
            });
        };

        const returnToPreviousPage = () => {
            const previousPath = sessionStorage.getItem(
                "network_previous_path"
            );

            // If there is no previous page, simply go to home.
            if (!previousPath) {
                if (window.location.pathname === NO_NETWORK_PATH) {
                    navigate("/", {
                        replace: true,
                    });
                }

                return;
            }

            sessionStorage.removeItem("network_previous_path");

            navigate(previousPath, {
                replace: true,
            });
        };

        const updateStatus = (status: boolean) => {
            setIsOnline(status);

            localStorage.setItem(
                "isOnline",
                status ? "true" : "false"
            );

            if (!status) {
                goToNoNetworkPage();
            } else {
                returnToPreviousPage();
            }
        };

        const checkConnection = async () => {
            const status = await checkInternetConnection();

            updateStatus(status);
        };

        // Check immediately when the provider mounts.
        checkConnection();

        // Continue checking periodically.
        const timer = window.setInterval(
            checkConnection,
            interval
        );

        const handleOnline = () => {
            checkConnection();
        };

        const handleOffline = () => {
            updateStatus(false);
        };

        window.addEventListener(
            "online",
            handleOnline
        );

        window.addEventListener(
            "offline",
            handleOffline
        );

        return () => {
            window.clearInterval(timer);

            window.removeEventListener(
                "online",
                handleOnline
            );

            window.removeEventListener(
                "offline",
                handleOffline
            );
        };
    }, [interval, navigate, location.pathname, location.search, location.hash]);

    return (
        <NetworkContext.Provider value={{ isOnline }}>
            {children}
        </NetworkContext.Provider>
    );
};

export const useNetwork = () =>
    useContext(NetworkContext);